import { useCallback, useMemo } from 'react'
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import type Draft from '~publish/legacy/composer/composer/entities/Draft/Draft'
import ComposerActionCreators from '~publish/legacy/composer/composer/action-creators/ComposerActionCreators'

export interface UseThreadDragDropProps {
  draft: Draft
}

export interface UseThreadDragDropReturn {
  sensors: ReturnType<typeof useSensors>
  handleDragEnd: (event: DragEndEvent) => void
  sortableIds: string[]
  modifiers: (typeof restrictToVerticalAxis)[]
  collisionDetection: typeof closestCenter
  DndContext: typeof DndContext
  SortableContext: typeof SortableContext
  verticalListSortingStrategy: typeof verticalListSortingStrategy
}

export function useThreadDragDrop({
  draft,
}: UseThreadDragDropProps): UseThreadDragDropReturn {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  const sortableIds = useMemo(() => {
    if (!draft.hasThread()) {
      return []
    }

    const ids = draft.thread.map((_threadItem, index) => {
      return `thread-${index}`
    })

    return ids
  }, [draft.thread])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over || active.id === over.id) {
        return
      }

      const activeIndex = parseInt(active.id.toString().split('-')[1])
      const overIndex = parseInt(over.id.toString().split('-')[1])

      if (activeIndex === overIndex) {
        return
      }

      const currentThread = draft.thread || []

      const reorderedThread = arrayMove(currentThread, activeIndex, overIndex)

      ComposerActionCreators.updateThreadOrder(draft.id, reorderedThread)
    },
    [draft],
  )

  return {
    sensors,
    handleDragEnd,
    sortableIds,
    modifiers: [restrictToVerticalAxis],
    collisionDetection: closestCenter,
    DndContext,
    SortableContext,
    verticalListSortingStrategy,
  }
}
